/**
 * Soft-Enforcement Suggestions — Phase 1c
 * ────────────────────────────────────────
 * The dataset that justifies (or refutes) every future hard-mode
 * threshold. Without this, soft mode is just delayed hard mode.
 *
 * Lifecycle:
 *   pending  → accepted     (operator clicked the manual-confirm path)
 *           → dismissed    (operator explicitly closed it)
 *           → overridden   (operator took a *different* action)
 *           → ignored_expired  (auto-transition after TTL — itself a signal)
 *
 * Storage: separate sink (NOT FrictionLog, NOT contractor telemetry).
 * In-memory module store; AppDataContext can persist it later in 1c.2.
 *
 * Mode behavior:
 *   • soft: every would-be auto-action emits a `pending` suggestion.
 *           Operator must take the manual-confirm path to action it.
 *   • hard: same suggestion is emitted AND the side-effect is auto-
 *           executed. The suggestion lands as `accepted` immediately.
 *   In both modes, the writer code path is identical — the difference
 *   is whether the calling site also invokes the side-effect writer.
 */

import { getNetworkEnforcementMode } from "../enforcementMode";

export type SuggestionKind =
  | "auto_holdback"
  | "auto_block_contractor"
  | "auto_escalate_overdue"
  | "override_rereview_required"
  | "auto_pause_holdback_for_dispute";

export type SuggestionOutcome =
  | "pending"
  | "accepted"
  | "dismissed"
  | "overridden"
  | "ignored_expired";

export interface SoftEnforcementSuggestion {
  id: string;
  kind: SuggestionKind;
  /** Logical subject — job id, contractor id, callback id, etc. */
  subjectId: string;
  subjectKind: "job" | "contractor" | "callback" | "holdback" | "observation";
  /** Brief machine reason key. UI maps to copy. Never leak PII or pricing. */
  rationaleKey: string;
  suggestedAt: string;
  /** Stable hash for dedupe — same subject + kind + rationaleKey within TTL. */
  payloadHash: string;
  outcome: SuggestionOutcome;
  outcomeAt?: string;
  outcomeActorId?: string;
  outcomeActorRole?: string;
  outcomeReason?: string;
  /** Mode at time of emission — informs cohort analysis. */
  enforcementModeAtEmit: "soft" | "hard";
  /** TTL for auto-expiry to ignored_expired. */
  expiresAt: string;
}

const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7d
const DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h dedupe window per (subject, kind, rationale)

const FORBIDDEN_KEYS = [
  "contractValue",
  "payoutAmount",
  "customerEmail",
  "customerPhone",
  "ssn",
] as const;

function sanitizeReason(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  let cleaned = reason;
  for (const k of FORBIDDEN_KEYS) {
    cleaned = cleaned.replace(new RegExp(`\\b${k}\\b`, "gi"), "[redacted]");
  }
  return cleaned;
}

function freshId(prefix: string, nowMs: number): string {
  return `${prefix}-${nowMs}-${Math.random().toString(36).slice(2, 8)}`;
}

function payloadHash(kind: string, subjectId: string, rationaleKey: string): string {
  // Cheap deterministic hash — sufficient for in-memory dedupe.
  return `${kind}|${subjectId}|${rationaleKey}`;
}

export interface EmitSuggestionInput {
  suggestions: SoftEnforcementSuggestion[];
  kind: SuggestionKind;
  subjectId: string;
  subjectKind: SoftEnforcementSuggestion["subjectKind"];
  rationaleKey: string;
  nowIso?: string;
  id?: string;
}

export interface EmitSuggestionResult {
  suggestions: SoftEnforcementSuggestion[];
  suggestion: SoftEnforcementSuggestion | null;
  deduped: boolean;
}

/**
 * Emit a suggestion. Idempotent within the dedupe window.
 * Pure — caller persists.
 */
export function emitSoftEnforcementSuggestion(
  input: EmitSuggestionInput,
): EmitSuggestionResult {
  const now = input.nowIso ?? new Date().toISOString();
  const nowMs = Date.parse(now);
  const hash = payloadHash(input.kind, input.subjectId, input.rationaleKey);

  const dup = input.suggestions.find(
    (s) =>
      s.payloadHash === hash &&
      s.outcome === "pending" &&
      nowMs - Date.parse(s.suggestedAt) <= DEDUPE_WINDOW_MS,
  );
  if (dup) return { suggestions: input.suggestions, suggestion: dup, deduped: true };

  const next: SoftEnforcementSuggestion = {
    id: input.id ?? freshId("ses", nowMs),
    kind: input.kind,
    subjectId: input.subjectId,
    subjectKind: input.subjectKind,
    rationaleKey: input.rationaleKey,
    suggestedAt: now,
    payloadHash: hash,
    outcome: "pending",
    enforcementModeAtEmit: getNetworkEnforcementMode(),
    expiresAt: new Date(nowMs + PENDING_TTL_MS).toISOString(),
  };
  return { suggestions: [...input.suggestions, next], suggestion: next, deduped: false };
}

export interface RecordSuggestionOutcomeInput {
  suggestions: SoftEnforcementSuggestion[];
  suggestionId: string;
  outcome: Exclude<SuggestionOutcome, "pending" | "ignored_expired">;
  actorId: string;
  actorRole: string;
  reason?: string;
  nowIso?: string;
}

export interface RecordSuggestionOutcomeResult {
  suggestions: SoftEnforcementSuggestion[];
  updated: SoftEnforcementSuggestion | null;
  error?: string;
}

export function recordSuggestionOutcome(
  input: RecordSuggestionOutcomeInput,
): RecordSuggestionOutcomeResult {
  const idx = input.suggestions.findIndex((s) => s.id === input.suggestionId);
  if (idx < 0) return { suggestions: input.suggestions, updated: null, error: "not_found" };
  const target = input.suggestions[idx];
  if (target.outcome !== "pending") {
    return { suggestions: input.suggestions, updated: null, error: "already_terminal" };
  }
  if (!input.actorId) {
    return { suggestions: input.suggestions, updated: null, error: "missing_actor_id" };
  }
  const updated: SoftEnforcementSuggestion = {
    ...target,
    outcome: input.outcome,
    outcomeAt: input.nowIso ?? new Date().toISOString(),
    outcomeActorId: input.actorId,
    outcomeActorRole: input.actorRole,
    outcomeReason: sanitizeReason(input.reason),
  };
  const next = [...input.suggestions];
  next[idx] = updated;
  return { suggestions: next, updated };
}

/** Sweep — flips expired pending suggestions to ignored_expired. Pure. */
export function sweepExpiredSuggestions(
  suggestions: SoftEnforcementSuggestion[],
  nowIso?: string,
): SoftEnforcementSuggestion[] {
  const nowMs = nowIso ? Date.parse(nowIso) : Date.now();
  let changed = false;
  const next = suggestions.map((s) => {
    if (s.outcome !== "pending") return s;
    if (Date.parse(s.expiresAt) > nowMs) return s;
    changed = true;
    return {
      ...s,
      outcome: "ignored_expired" as SuggestionOutcome,
      outcomeAt: new Date(nowMs).toISOString(),
    };
  });
  return changed ? next : suggestions;
}

/** Aggregate acceptance/dismissal/override rates for a suggestion kind. */
export interface SuggestionKindStats {
  kind: SuggestionKind;
  total: number;
  pending: number;
  accepted: number;
  dismissed: number;
  overridden: number;
  ignoredExpired: number;
  /** Of terminal items, share that were accepted. null if no terminal items. */
  acceptanceRate: number | null;
  /** Of terminal items, share where operator did *something else* — highest signal. */
  overrideRate: number | null;
}

export function getSuggestionStats(
  suggestions: SoftEnforcementSuggestion[],
  kind?: SuggestionKind,
): SuggestionKindStats[] {
  const kinds: SuggestionKind[] = kind
    ? [kind]
    : Array.from(new Set(suggestions.map((s) => s.kind)));
  return kinds.map((k) => {
    const own = suggestions.filter((s) => s.kind === k);
    const counts = {
      pending: 0,
      accepted: 0,
      dismissed: 0,
      overridden: 0,
      ignored_expired: 0,
    } as Record<SuggestionOutcome, number>;
    for (const s of own) counts[s.outcome] += 1;
    const terminal =
      counts.accepted + counts.dismissed + counts.overridden + counts.ignored_expired;
    return {
      kind: k,
      total: own.length,
      pending: counts.pending,
      accepted: counts.accepted,
      dismissed: counts.dismissed,
      overridden: counts.overridden,
      ignoredExpired: counts.ignored_expired,
      acceptanceRate: terminal === 0 ? null : counts.accepted / terminal,
      overrideRate: terminal === 0 ? null : counts.overridden / terminal,
    };
  });
}
